import Image from 'next/image'
import styles from '../styles/FNOLAutomation.module.css'
import SEO from '../components/SEO'
import { useRef, useState } from 'react'
import { 
    howItWorksSteps, 
    keyBenefits, 
    stats, 
    automationFeatures 
} from '../const/fnol-automation'
import { questions } from '../const/faq'
import ContactUsCTA from '../components/ContactUsCTA'

export default function FNOLAutomation({ locale }) {
  const QuestionSection = ({ keyIndex, que, ans }) => {
    const [isOpen, setIsOpen] = useState(false)
    return (
      <div
        className={`${styles.questionModal} ${isOpen ? styles.openModal : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <p className={styles.questionIndex}>
          {String(keyIndex + 1).padStart(2, '0')}
        </p>
        <div className={styles.question}>
          <p>{que}</p>
          <Image
            src={isOpen ? '/img/shrink-icon.svg' : '/img/expand-icon.svg'}
            alt="FAQ"
            width={36}
            height={36}
            objectFit="contain"
          />
        </div>
        <div className={`${styles.answer} ${isOpen ? styles.open : ''}`}>
          {ans.map((item, index) => (
            <p key={index}>{item}</p>
          ))}
        </div>
      </div>
    )
  }
  const [hoveredStat, setHoveredStat] = useState(null)
  
  const handleItemEnter = (index) => setHoveredStat(index)

  const handleItemLeave = (e) => {
    const overlay = e.currentTarget.parentElement.querySelector('[data-overlay]')
    if (e.relatedTarget && overlay && overlay.contains(e.relatedTarget)) return
    setHoveredStat(null)
  }

  const handleOverlayLeave = (e) => {
    const isGoingToItem = [...e.currentTarget.parentElement.querySelectorAll('[data-item]')]
      .some(el => el === e.relatedTarget || el.contains(e.relatedTarget))
    if (isGoingToItem) return
    setHoveredStat(null)
  }

  return (
    <div className={styles.container}>
      <SEO
        title="FNOL Automation in Insurance | First Notice of Loss AI Solution | Inspektlabs"
        description="Accelerate first notice of loss with AI-powered FNOL automation. Inspektlabs captures damage photos, extracts claim data & triggers workflows instantly — cutting FNOL time by 70%."
        image="https://inspektlabs.com/img/car.png"
        url="https://inspektlabs.com/fnol-automation"
        page="fnol-automation"
      />
      <main className={styles.main}>

        {/* Hero Section */}
        <section className={styles.featured}>
          <div className={styles.heroContent}>
            <div className={styles.grid}>
              <h1 className={styles.featuredTitle}>
              FNOL Automation in Insurance: AI-Powered First Notice of Loss Processing
              </h1>
              <p className={styles.featuredText}>
                Inspektlabs delivers complete FNOL automation that transforms first notice of loss processing from hours to minutes through AI-powered vehicle inspection.
                <br /><br />
                Policyholders submit claims via SMS links with guided photo capture, and AI generates comprehensive damage reports within seconds, reducing claims processing time by 98% while improving customer satisfaction.
              </p>
              <ContactUsCTA as="div" cluster = "fnol-automation" className={styles.mainBtn}>
                Request a Demo
                <Image src='/img/arrow right white.png' alt="Arrow" width={20} height={20} className={styles.callImg} />
              </ContactUsCTA>
              
            </div>
            <div className={styles.heroImage}>
              <Image
                src="/img/fnol main-img.png"
                alt="FNOL Vehicle"
                layout="fill"
                objectFit="contain"
                objectPosition="center right"
                priority
              />
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className={styles.howItWorksSection}>
          <div className={styles.howItWorksContainer}>
            <p className={styles.sectionLabel}>CAPABILITIES</p>
            <h2 className={styles.sectionTitle}>
            How FNOL Automation Enables Touchless Claims Processing
            </h2>
            <p className={styles.sectionSubtitle}>
              Our FNOL automation platform enables instant first notice of loss submission and assessment without human intervention.
            </p>

            <div className={styles.stepsWrapper}>
            <div className={styles.stepsGrid}>
              {howItWorksSteps.map((step, index) => (
                <div key={index} className={styles.stepCard}>

                  <div className={styles.stepIconWrapper}>
                    <img src={step.icon} alt={step.step} width={100} height={100} />
                  </div>

                  <div className={styles.stepConnectorRow}>
                    <div className={`${styles.stepLine} ${index === 0 ? styles.stepLineHidden : ''}`}></div>
                    <div className={styles.stepDot}></div>
                    <p className={styles.stepLabel}>{step.step}</p>
                    <div className={`${styles.stepLine} ${index === howItWorksSteps.length - 1 ? styles.stepLineHidden : ''}`}></div>
                  </div>

                  <p className={styles.stepText}>{step.text}</p>

                </div>
              ))}
            </div>
          </div>
          </div>
        </section>

        {/* Key Benefits Section */}
        <section className={styles.keyBenefitsSection}>
          <div className={styles.keyBenefitsContainer}>
            <h2 className={styles.benefitsSectionTitle}>Key Benefits of FNOL Automation for Insurers</h2>
            <div className={styles.capabilitiesGrid}>
                {keyBenefits.map((benefit, idx) => (
                    <div className={styles.capabilityCard} key={idx}>
                        <div className={styles.capabilityIcon}>
                            <Image 
                                src={benefit.icon} 
                                alt={benefit.title} 
                                layout="fill"
                                objectFit="contain"
                                objectPosition="center right"
                            />
                        </div>
                        <div className={styles.capabilityContent}>
                            <h3 className={styles.capabilityTitle}>{benefit.title}</h3>
                            <p className={styles.capabilityText}>{benefit.text}</p>
                        </div>
                    </div>
                ))}
            </div>
          </div>
        </section>

        {/* Complete Automation Section */}
        <section className={styles.automationSection}>
          <div className={styles.automationContainer}>
            <h2 className={styles.automationTitle}>
            End-to-End Insurance Claim Automation: From FNOL to Settlement 4. Why Modern Insurers Are Adopting FNOL Automation
            </h2>
            <p className={styles.automationSubtitle}>
              Our FNOL automation platform integrates seamlessly with existing claims management systems for complete workflow automation.
            </p>
            <div className={styles.automationGrid}>
              {automationFeatures.map((feature, index) => (
                <div key={index} className={styles.automationCard}>
                  <div className={styles.automationIcon}>
                    <img src={feature.icon} alt={feature.title} width={80} height={80} onError={(e) => {
                      e.target.style.display = 'none'
                    }} />
                  </div>
                  <div className={styles.automationCardContent}>
                    <h3 className={styles.automationCardTitle}>{feature.title}</h3>
                    <p className={styles.automationCardText}>{feature.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className={styles.automationText}>
            These inefficiencies add up to significant costs and lost opportunities
        </p>
        </section>

        {/* Stats Section */}
        <section className={styles.statsSection}>
            <div className={styles.statsContainer}>
                <h2 className={styles.statsTitle}>
                    Why should Modern insurace consider FNOL Automation
                </h2>
                <p className={styles.statsSubtitle}>
                    Leading insuracne carriers deploy our first notice of loss automation to reduce costs and accelerate customer service.
                </p>
                <div className={styles.statsGrid}>
                  {stats.map((stat, index) => (
                    <div
                      key={index}
                      data-item
                      className={styles.statItem}
                      onMouseEnter={() => handleItemEnter(index)}
                      onMouseLeave={handleItemLeave}
                    >
                      <div className={`${styles.statFront} ${hoveredStat !== null ? styles.statFrontHidden : ''}`}>
                        <h3 className={styles.statValue}>{stat.value}</h3>
                        <p className={styles.statLabel}>{stat.label}</p>
                      </div>
                    </div>
                  ))}

                  <div
                    data-overlay
                    className={`${styles.statHover} ${hoveredStat !== null ? styles.statHoverVisible : ''}`}
                    onMouseLeave={handleOverlayLeave}
                  >
                    {hoveredStat !== null && (
                      <>
                        <div className={styles.statHoverLeft}>
                          <h3 className={styles.statHoverValue}>{stats[hoveredStat].value}</h3>
                          <p className={styles.statHoverLabel}>{stats[hoveredStat].label}</p>
                        </div>
                        <div className={styles.statHoverDivider}></div>
                        <p className={styles.statHoverDesc}>{stats[hoveredStat].description}</p>
                      </>
                    )}
                  </div>
                </div>
            </div>
        </section>

        {/* FAQ Section */}
        <section className={styles.faqSectionContainer}>
          <div className={styles.faqSection}>
            <div className={styles.faqTitleContainer}>
              <h2 className={styles.faqTitle}>Commonly asked questions</h2>
            </div>
            <div className={styles.questionContainer}>
              <div className={styles.faqGrid}>
                {questions["FNOL"][locale].map((item, index) => (
                  <QuestionSection keyIndex={index} que={item.Q} ans={item.A} key={index} />
                ))}
              </div>
            </div>
            <div
              className={styles.viewMoreBtn}
              style={{ width: '200px' }}
              onClick={() => (window.location.href = '/faq')}
            >
              View More
            </div>
          </div>
        </section>

      </main>
    </div>
  )
}